<!-- 顶部悬浮提示组件：用于展示成功/警告/错误等轻量提示信息 -->
<template>
  <transition name="planet-toast-fade">
    <div
      v-if="internalVisible"
      class="planet-toast"
      :class="`planet-toast--${type}`"
    >
      <div class="planet-toast__icon">
        <span v-if="type === 'success'">✓</span>
        <span v-else-if="type === 'warning'">!</span>
        <span v-else-if="type === 'error'">×</span>
        <span v-else>i</span>
      </div>
      <div class="planet-toast__content">
        <div
          v-if="message"
          class="planet-toast__message"
        >
          <span class="planet-toast__message-text">{{ message }}</span>
          <span
            v-if="showDots"
            class="planet-toast__dots"
            :class="{ 'is-paused': !loading }"
            aria-hidden="true"
          >
            <span class="dot dot-1">.</span>
            <span class="dot dot-2">.</span>
            <span class="dot dot-3">.</span>
          </span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'PlanetToast',
  // 让 Vue2 的 v-model 绑定到 visible / update:visible（而不是默认的 value / input）
  model: {
    prop: 'visible',
    event: 'update:visible'
  },
  props: {
    type: {
      type: String,
      default: 'info' // info | success | warning | error
    },
    message: {
      type: String,
      default: ''
    },
    duration: {
      type: Number,
      default: 2500
    },
    // 是否在 message 后展示“三个点”
    showDots: {
      type: Boolean,
      default: false
    },
    // 三个点是否播放动画（false 时暂停在当前帧）
    loading: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      internalVisible: false,
      timer: null
    }
  },
  watch: {
    visible (val) {
      this.internalVisible = val
    },
    // 当 message / duration 在显示中发生变化时，重新计时（避免“持续显示”状态下后续 toast 不自动消失）
    message () {
      if (this.internalVisible) this.startTimer()
    },
    duration () {
      if (this.internalVisible) this.startTimer()
    },
    internalVisible (val) {
      if (val) {
        this.startTimer()
      } else {
        this.clearTimer()
        this.$emit('update:visible', false)
      }
    }
  },
  mounted () {
    if (this.visible) {
      this.internalVisible = true
    }
  },
  methods: {
    startTimer () {
      this.clearTimer()
      if (this.duration <= 0) return
      this.timer = setTimeout(() => {
        this.internalVisible = false
      }, this.duration)
    },
    clearTimer () {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    }
  },
  beforeDestroy () {
    this.clearTimer()
  }
}
</script>

<style scoped lang="scss">
.planet-toast {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2100;
  min-width: 260px;
  max-width: 420px;
  padding: 10px 16px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow:
    0 14px 40px rgba(15, 23, 42, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(129, 216, 208, 0.6);
}

.planet-toast__icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #81d8d0, #2a9686);
  box-shadow: 0 4px 12px rgba(42, 150, 134, 0.5);
  flex-shrink: 0;
}

.planet-toast--success .planet-toast__icon {
  background: linear-gradient(135deg, #63d58a, #3cb371);
  box-shadow: 0 4px 12px rgba(99, 213, 138, 0.55);
}

.planet-toast--warning .planet-toast__icon {
  background: linear-gradient(135deg, #ffb74d, #ff9800);
  box-shadow: 0 4px 12px rgba(255, 183, 77, 0.55);
}

.planet-toast--error .planet-toast__icon {
  background: linear-gradient(135deg, #ff6b81, #ff5252);
  box-shadow: 0 4px 12px rgba(255, 107, 129, 0.55);
}

.planet-toast__content {
  display: flex;
  flex-direction: row;
  min-width: 0;
}

.planet-toast__message {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.4;
  max-height: 3.2em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.planet-toast__message-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planet-toast__dots {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
}

.planet-toast__dots .dot {
  display: inline-block;
  width: 6px;
  text-align: center;
  opacity: 0.25;
  animation: planet-toast-dot 1s infinite ease-in-out;
}

.planet-toast__dots .dot-2 { animation-delay: 0.15s; }
.planet-toast__dots .dot-3 { animation-delay: 0.30s; }

.planet-toast__dots.is-paused .dot {
  animation-play-state: paused;
  opacity: 0.65;
}

@keyframes planet-toast-dot {
  0% { opacity: 0.25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-1px); }
  60% { opacity: 0.25; transform: translateY(0); }
  100% { opacity: 0.25; transform: translateY(0); }
}

.planet-toast-fade-enter-active,
.planet-toast-fade-leave-active {
  transition:
    opacity 0.18s ease-out,
    transform 0.18s ease-out;
}

.planet-toast-fade-enter,
.planet-toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}
</style>
